/* eslint-disable */
/**
 * Form GTM Tracking
 * Tracks form submission with all collected interaction data
 */

// Store lightweight form metadata for interaction events
const sessionData = {
    formStartTime: Date.now()
};

/**
 * Get current form metadata for GTM events
 * @returns {{ formName: string, formId: string }}
 */
function getFormMetadata() {
    const form = document.querySelector('.umbraco-forms-rds form');
    if (!form) {
        return {
            formName: 'Unknown Form',
            formId: 'unknown'
        };
    }

    return {
        formName: form.querySelector('[name="FormName"]')?.value || 'Unknown Form',
        formId: form.querySelector('[name="FormId"]')?.value || 'unknown'
    };
}

/**
 * Push event to GTM dataLayer with callback support
 * @param {Object} eventData - The event data to push
 * @param {Function} callback - Optional callback to execute after event is pushed
 */
function pushToDataLayer(eventData, callback) {
    if (typeof window.dataLayer !== 'undefined') {
        if (callback && typeof callback === 'function') {
            eventData.eventCallback = function() {
                console.log('GTM Event Callback fired');
                callback();
            };
            eventData.eventTimeout = 2000;
        }

        window.dataLayer.push(eventData);
        console.log('GTM Event Pushed:', eventData);

        if (!callback) {
            return;
        }

        setTimeout(function() {
            if (callback) {
                console.log('GTM Event Callback timeout reached, executing fallback');
                callback();
            }
        }, 2100);

    } else {
        console.warn('dataLayer not available, event not tracked:', eventData);
        if (callback && typeof callback === 'function') {
            callback();
        }
    }
}

/**
 * Get the question text from a fieldset
 * @param {HTMLElement} fieldset - The fieldset element
 * @returns {string} The question text
 */
function getQuestionText(fieldset) {
    const legend = fieldset.querySelector('legend');
    if (legend) {
        return legend.textContent.trim();
    }
    return 'Unknown Question';
}

/**
 * Handle radio button change
 * @param {Event} event - The change event
 */
function handleRadioChange(event) {
    const radio = event.target;
    const fieldset = radio.closest('fieldset');

    if (!fieldset) return;

    const questionText = getQuestionText(fieldset);
    const answer = radio.value;

    const formMeta = getFormMetadata();
    pushToDataLayer({
        event: 'form_radio_interaction',
        form_id: formMeta.formId,
        form_name: formMeta.formName,
        question_text: questionText,
        answer_value: answer,
        field_id: radio.id || '',
        field_name: radio.name || '',
        form_time_spent_seconds: Math.round((Date.now() - sessionData.formStartTime) / 1000),
        page_url: window.location.href,
        page_path: window.location.pathname
    });

    console.log('Radio interaction tracked:', questionText, '=', answer);
}

/**
 * Handle "Read More" button click
 * @param {Event} event - The click event
 */
function handleReadMoreClick(event) {
    const button = event.currentTarget;
    const fieldset = button.closest('fieldset');

    if (!fieldset) return;

    const questionText = getQuestionText(fieldset);
    const isExpanding = button.getAttribute('aria-expanded') !== 'true';

    console.log('Read More click observed:', questionText, isExpanding ? 'expanded' : 'collapsed');
}

/**
 * Handle link clicks within form content
 * @param {Event} event - The click event
 */
function handleFormLinkClick(event) {
    const link = event.currentTarget;
    const linkText = link.textContent.trim();
    const linkUrl = link.href;

    const isSubmitEnquiryLink = linkText.toLowerCase().includes('submit an enquiry') ||
                               link.title?.toLowerCase().includes('submit an enquiry');

    const formMeta = getFormMetadata();

    if (isSubmitEnquiryLink) {
        event.preventDefault();

        console.log('Submit enquiry link clicked, tracking interaction...');

        let hasNavigated = false;

        const navigateToDestination = function() {
            if (!hasNavigated) {
                hasNavigated = true;
                console.log('Navigating to:', linkUrl);
                window.location.href = linkUrl;
            }
        };

        pushToDataLayer({
            event: 'form_link_interaction',
            form_id: formMeta.formId,
            form_name: formMeta.formName,
            link_text: linkText,
            link_url: linkUrl,
            link_type: 'submit_enquiry',
            context: 'happy_path',
            form_time_spent_seconds: Math.round((Date.now() - sessionData.formStartTime) / 1000),
            page_url: window.location.href,
            page_path: window.location.pathname
        }, navigateToDestination);

        return;
    }

    const isEdrisLink = linkText.toLowerCase().includes('learn more about the services edris offer') ||
                       linkUrl.includes('how-rds-works-with-edris');

    const contentSection = link.closest('[id^="content-"]');
    let contextType = 'unknown';

    if (contentSection) {
        const sectionId = contentSection.id;
        if (sectionId.includes('happyPath') || sectionId.includes('happy-path')) {
            contextType = 'happy_path';
        } else if (sectionId.includes('unhappy')) {
            contextType = 'unhappy_path';
        }
    }

    const modalContent = link.closest('.learn-more-modal');
    let linkContext = 'unknown';
    let questionContext = null;

    if (modalContent) {
        linkContext = 'modal';
        const fieldset = modalContent.closest('fieldset');
        questionContext = fieldset ? getQuestionText(fieldset) : null;
    } else if (contentSection) {
        linkContext = contextType;
    }

    pushToDataLayer({
        event: 'form_link_interaction',
        form_id: formMeta.formId,
        form_name: formMeta.formName,
        link_text: linkText,
        link_url: linkUrl,
        link_type: isEdrisLink ? 'edris_services' : 'general',
        context: linkContext,
        question_context: questionContext || '',
        form_time_spent_seconds: Math.round((Date.now() - sessionData.formStartTime) / 1000),
        page_url: window.location.href,
        page_path: window.location.pathname
    });

    console.log('Link interaction tracked:', linkText, 'in', linkContext);
}

/**
 * Initialize radio button tracking
 */
function initRadioTracking() {
    const radioButtons = document.querySelectorAll('.umbraco-forms-rds input[type="radio"]');

    radioButtons.forEach(radio => {
        radio.removeEventListener('change', handleRadioChange);
        radio.addEventListener('change', handleRadioChange);
    });

    console.log('Radio button tracking initialized for', radioButtons.length, 'radio buttons');
}

/**
 * Initialize "Read More" button tracking
 */
function initReadMoreTracking() {
    const readMoreButtons = document.querySelectorAll('.umbraco-forms-rds .learn-more');

    readMoreButtons.forEach(button => {
        button.removeEventListener('click', handleReadMoreClick);
        button.addEventListener('click', handleReadMoreClick);
    });

    console.log('Read More tracking initialized for', readMoreButtons.length, 'buttons');
}

/**
 * Initialize link tracking within forms
 */
function initFormLinkTracking() {
    const formLinks = document.querySelectorAll('.umbraco-forms-rds a[href]');

    formLinks.forEach(link => {
        link.removeEventListener('click', handleFormLinkClick);
        link.addEventListener('click', handleFormLinkClick);
    });

    console.log('Form link tracking initialized for', formLinks.length, 'links');
}

/**
 * Initialize all form tracking
 */
export function initFormTracking() {
    initRadioTracking();
    initReadMoreTracking();
    initFormLinkTracking();
}

/**
 * Set up MutationObserver for dynamic form content
 */
export function initFormObserver() {
    const observer = new MutationObserver(function(mutations) {
        mutations.forEach(function(mutation) {
            if (mutation.type === 'childList' || mutation.type === 'attributes') {
                clearTimeout(window.formTrackingTimeout);
                window.formTrackingTimeout = setTimeout(initFormTracking, 300);
            }
        });
    });

    const formContainer = document.querySelector('.umbraco-forms-rds');
    if (formContainer) {
        observer.observe(formContainer, {
            childList: true,
            subtree: true,
            attributes: true,
            attributeFilter: ['class', 'style']
        });
    }
}

